import { CommonModule } from '@angular/common';
import { Clipboard } from '@angular/cdk/clipboard';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FeatherModule } from 'angular-feather';
import { finalize } from 'rxjs';
import {
  UserDirectoryEntry,
  UserService
} from '../services/api-services/user.service';

@Component({
  selector: 'app-users',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    FeatherModule,
    MatButtonModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    MatTooltipModule
  ],
  templateUrl: './users.component.html',
  styleUrl: './users.component.scss'
})
export class UsersComponent implements OnInit, OnDestroy {
  users: UserDirectoryEntry[] = [];
  filteredUsers: UserDirectoryEntry[] = [];
  searchTerm = '';
  isLoading = false;
  errorMessage: string | null = null;
  copiedUsername: string | null = null;

  private copiedResetTimer?: ReturnType<typeof setTimeout>;

  constructor(
    private readonly userService: UserService,
    private readonly clipboard: Clipboard,
    private readonly snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.loadUsers();
  }

  ngOnDestroy(): void {
    if (this.copiedResetTimer) {
      clearTimeout(this.copiedResetTimer);
    }
  }

  loadUsers(): void {
    this.isLoading = true;
    this.errorMessage = null;

    this.userService.getUserDirectory()
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: response => {
          this.users = response.users;
          this.applyFilter();
        },
        error: error => {
          this.users = [];
          this.filteredUsers = [];
          this.errorMessage = 'Unable to load the user directory.';
          console.error(error);
        }
      });
  }

  applyFilter(): void {
    const normalizedSearch = this.searchTerm.trim().toLocaleLowerCase();

    this.filteredUsers = normalizedSearch
      ? this.users.filter(user =>
          user.username.toLocaleLowerCase().includes(normalizedSearch)
        )
      : [...this.users];
  }

  copyUsername(username: string): void {
    if (!this.clipboard.copy(username)) {
      this.snackBar.open('Could not copy the username.', 'Dismiss', {
        duration: 4000,
        horizontalPosition: 'end',
        verticalPosition: 'top'
      });
      return;
    }

    this.copiedUsername = username;
    this.snackBar.open(`Copied "${username}" to the clipboard.`, 'Dismiss', {
      duration: 2500,
      horizontalPosition: 'end',
      verticalPosition: 'top'
    });

    if (this.copiedResetTimer) {
      clearTimeout(this.copiedResetTimer);
    }
    this.copiedResetTimer = setTimeout(() => {
      if (this.copiedUsername === username) {
        this.copiedUsername = null;
      }
    }, 2500);
  }

  usernameInitial(username: string): string {
    return username.trim().charAt(0).toLocaleUpperCase();
  }

  trackByUsername(_index: number, user: UserDirectoryEntry): string {
    return user.username;
  }
}
